/**
 * Map raw application / history stage strings → canonical funnel ids.
 * Kept aligned with `server/recruitmentDashboard.cjs` (statusToCanonical + DEFAULT_STAGE_ALIASES).
 */

const CANONICAL_STAGES = [
  'applied',
  'shortlisted',
  'cv_sent',
  'interview_1',
  'interview_further',
  'offer',
  'placement',
] as const;

export type ReportingCanonicalStage = (typeof CANONICAL_STAGES)[number];

const REJECTED = new Set([
  'afgewezen',
  'rejected',
  'declined',
  'geweigerd',
  'closed_lost',
  'niet geselecteerd',
  'withdrawn',
  'no show',
  'afgewezen door opdrachtgever',
  'automatisch afwijzen',
  'geen feedback klant',
  'negatief klant',
  'niet voorgesteld',
  'nog af te wijzen',
  'offer rejected by candidate',
  'rejected by client',
  'kandidaat teruggetrokken',
  'opdracht ingetrokken',
]);

const ALIASES: Record<ReportingCanonicalStage, string[]> = {
  applied: ['new', 'applied', 'application', 'sollicitatie', 'ingediend', 'draft', 'lead'],
  shortlisted: ['shortlist', 'op shortlist', 'op_shortlist', 'shortlisted', 'lead'],
  cv_sent: [
    'voorgesteld',
    'cv sent',
    'cv_sent',
    'submitted',
    'draft_cv',
    'voorstel',
    'voorgelegd',
    'sendout',
    'presented',
    'client submission',
    'voorstellen',
    'warme stoel',
    'positief klant',
  ],
  interview_1: [
    'gesprek',
    'interview',
    '1e gesprek',
    'eerste gesprek',
    'first interview',
    'screening',
    '1e gesprek(zonder mail)',
    'interview bevestiging mailen',
    'bevestiging niet mailen',
    'client interview',
  ],
  interview_further: [
    '2e gesprek',
    '3e gesprek',
    '4e gesprek',
    '5e gesprek',
    'tweede gesprek',
    'derde gesprek',
    'vervolg',
    'vervolg gesprek',
    'further interview',
    'interview 2',
    'interview2',
    'interview 3',
    'interview3',
    'case',
    'assessment',
    'final interview',
    'second interview',
    'third interview',
    'background check',
    'hiring manager sendout',
  ],
  offer: [
    'aanbod',
    'aanbieding',
    'offer',
    'contract',
    'onder contract',
    'offers',
    'offer sent',
    'offer extended',
    'offer accepted',
  ],
  placement: ['plaatsing', 'geplaatst', 'aangenomen', 'placed', 'hire', 'hired', 'ingestroomd'],
};

function norm(s: string | null | undefined): string {
  return String(s ?? '')
    .trim()
    .toLowerCase();
}

let lookup: Map<string, ReportingCanonicalStage> | null = null;

function aliasLookup(): Map<string, ReportingCanonicalStage> {
  if (lookup) return lookup;
  const m = new Map<string, ReportingCanonicalStage>();
  for (const stageId of CANONICAL_STAGES) {
    for (const raw of ALIASES[stageId] || []) {
      m.set(norm(raw), stageId);
    }
  }
  lookup = m;
  return m;
}

/**
 * Returns canonical stage or null when status is empty / rejected-like.
 */
export function reportingStatusToCanonical(status: string | null | undefined): ReportingCanonicalStage | null {
  const n = norm(status);
  if (!n || REJECTED.has(n)) return null;
  if ((CANONICAL_STAGES as readonly string[]).includes(n)) return n as ReportingCanonicalStage;
  const lu = aliasLookup();
  if (lu.has(n)) return lu.get(n)!;
  /** Before generic substring keys (e.g. "gesprek" → 1e), treat numbered / vervolg rounds as vervolg. */
  if (
    n.includes('2e') ||
    n.includes('3e') ||
    n.includes('4e') ||
    n.includes('5e') ||
    n.includes('tweede') ||
    n.includes('derde') ||
    n.includes('second interview') ||
    n.includes('third interview') ||
    n.includes('3rd interview') ||
    n.includes('final interview') ||
    n.includes('vervolg') ||
    n.includes('case') ||
    n.includes('assessment')
  )
    return 'interview_further';
  for (const [key, stageId] of lu) {
    if (n.includes(key) || key.includes(n)) return stageId;
  }
  if (n.includes('gesprek') || n.includes('interview') || n.includes('screening')) return 'interview_1';
  if (n.includes('aanbod') || n.includes('aanbieding') || n.includes('offer') || n.includes('contract'))
    return 'offer';
  if (n.includes('plaatsing') || n.includes('geplaatst') || n.includes('placed') || n.includes('hired') || n.includes('aangenomen'))
    return 'placement';
  return 'applied';
}

/** Kanban/shortlist UI stores 1e/2e/3e in cv_snapshot.pipeline_meta.gesprek_round (status may stay «Gesprek»). */
export function applicationGesprekRoundMeta(app: unknown): string | null {
  const a = app as Record<string, unknown> | null | undefined;
  if (!a) return null;
  const snap = a.cv_snapshot as Record<string, unknown> | undefined;
  const pmSnap = snap?.pipeline_meta as Record<string, unknown> | undefined;
  const pmTop = a.pipeline_meta as Record<string, unknown> | undefined;
  const data = a.data as Record<string, unknown> | undefined;
  const pmData = data?.pipeline_meta as Record<string, unknown> | undefined;
  const raw =
    pmSnap?.gesprek_round ??
    pmTop?.gesprek_round ??
    pmData?.gesprek_round;
  if (raw == null || raw === '') return null;
  return String(raw).trim();
}

/**
 * interview_1 vs interview_further for KPIs/donuts: uses gesprek_round when status is still generic «Gesprek».
 * Returns null if the application is not in an interview-like canonical stage.
 */
export function reportingInterviewCanonicalFromApplication(app: unknown): ReportingCanonicalStage | null {
  const statusCanon = reportingStatusToCanonical((app as { status?: string })?.status);
  if (statusCanon !== 'interview_1' && statusCanon !== 'interview_further') return null;
  const roundStr = applicationGesprekRoundMeta(app);
  if (roundStr != null && roundStr !== '') {
    const n = parseInt(roundStr, 10);
    if (Number.isFinite(n) && n >= 2) return 'interview_further';
    if (Number.isFinite(n) && n === 1) return 'interview_1';
  }
  return statusCanon;
}

/** Huidige pipeline: in een gespreksfase (canonical of losse status-string). */
export function applicationInGesprekPhase(app: unknown): boolean {
  const iv = reportingInterviewCanonicalFromApplication(app);
  if (iv === 'interview_1' || iv === 'interview_further') return true;
  const status = (app as { status?: string })?.status;
  return reportingStatusBucket(status) === 'gesprek';
}

/** 1e gesprek: canonical interview_1 of generieke «Gesprek»-status zonder 2e/3e/vervolg. */
export function applicationInFirstInterviewPhase(app: unknown): boolean {
  const iv = reportingInterviewCanonicalFromApplication(app);
  if (iv === 'interview_1') return true;
  if (iv === 'interview_further') return false;
  return reportingStatusBucket((app as { status?: string })?.status) === 'gesprek';
}

export function applicationInFurtherInterviewPhase(app: unknown): boolean {
  return reportingInterviewCanonicalFromApplication(app) === 'interview_further';
}

function reportingStatusBucket(
  status: string | undefined
): 'shortlist' | 'cvSent' | 'gesprek' | 'offer' | 'placed' | null {
  const l = String(status ?? '')
    .trim()
    .toLowerCase();
  if (!l) return null;
  if (
    l.includes('shortlist') ||
    l === 'new' ||
    l.includes('op shortlist')
  )
    return 'shortlist';
  if (l.includes('voorgesteld') || l.includes('voorstel') || l.includes('cv sent')) return 'cvSent';
  if (
    l.includes('gesprek') ||
    l.includes('interview') ||
    l.includes('vervolg') ||
    ['2e gesprek', 'tweede gesprek'].includes(l)
  )
    return 'gesprek';
  if (l.includes('aanbod') || l.includes('offer')) return 'offer';
  if (['plaatsing', 'geplaatst', 'aangenomen', 'hired', 'placed'].some((x) => l.includes(x))) return 'placed';
  return null;
}
